import { Box } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import queryString from 'query-string';
import { NON_EU_IMPORT_REPORT_TYPE, VAT_REPORTS } from 'redux/reducers/reports/constants';
import { getVatReportsRequest } from 'redux/reducers/reports/reducer';
import {
	getDownloadVatReportsLoader,
	getVatReports,
	getVatReportsLoader,
} from 'redux/reducers/reports/selectors';
import { TGetVatReportsResponseRowsItem, TVatReportTypes } from 'redux/reducers/reports/types';
import { PAGE_SIZE } from 'services/constants/env';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { ReportsDescription } from 'components/ReportsDescription';
import { getVatReportsTableStructure } from './tableStructure';
import {
	getDownloadVatReportAction,
	getSpecFormat,
	TVatReportDownloadButton,
} from './downloadActions';

export const VatReports: FC = () => {
	const params = useParams<{ typeOfReport?: string }>();
	const { typeOfReport } = params;
	const { search } = useLocation();
	const { page } = queryString.parse(search);

	const vatReports = VAT_REPORTS.find((item) => item.path === typeOfReport);
	const reportType = vatReports?.type as TVatReportTypes;

	const dispatch = useDispatch();
	const reports = useSelector(getVatReports);
	const reportsIsLoading = useSelector(getVatReportsLoader);
	const downloadReportLoader = useSelector(getDownloadVatReportsLoader);

	useEffect(() => {
		dispatch(
			getVatReportsRequest({
				reportType,
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
			}),
		);
	}, [dispatch, reportType, page]);

	const specFormat = getSpecFormat(reportType, reports?.emData);
	const tableStructure = useMemo(
		() => getVatReportsTableStructure(reportType, reports?.emData, specFormat),
		[reportType, reports?.emData, specFormat],
	);

	const handleAction = (id: number, button: string) => {
		const report = reports?.data?.find((item) => item.id === id);
		if (!report) return;

		const action = getDownloadVatReportAction(
			reportType,
			report,
			button as TVatReportDownloadButton,
			specFormat,
		);
		if (action) dispatch(action);
	};

	return (
		<Box>
			{!reportsIsLoading && !!reports?.data?.length && !!vatReports?.description && (
				<ReportsDescription description={vatReports?.description} />
			)}

			<EasTableWithoutFilter<TGetVatReportsResponseRowsItem>
				tableStructure={tableStructure}
				items={reports}
				itemsIsLoading={reportsIsLoading || downloadReportLoader}
				handleAction={handleAction}
				minWidth={reportType === NON_EU_IMPORT_REPORT_TYPE ? 600 : 1200}
			/>
		</Box>
	);
};
